import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { GROUPS_LIST, TEAMS_LIST, INITIAL_ISOLATED_DEALS, INITIAL_NOTIFICATIONS, INITIAL_FEED } from '../utils/constants';
import { groupsApi, notificationsApi, wixFeedApi, wixCommentsApi, wixLikesApi, bonndleGroupsApi } from '../services/api';
import { STORAGE_KEYS } from '../services/bonndleApi';

// Helper to get current user ID from localStorage
const getUserId = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.USER_ID) || null;
  } catch {
    return null;
  }
};

const AppStateContext = createContext(null);

// Helper to get persisted state from localStorage
const getPersistedState = (key, defaultValue) => {
  try {
    const stored = localStorage.getItem(`bonndle:${key}`);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

// Helper to persist state to localStorage
const persistState = (key, value) => {
  try {
    localStorage.setItem(`bonndle:${key}`, JSON.stringify(value));
  } catch {
    // Ignore storage errors
  }
};

export function AppStateProvider({ children }) {
  // Navigation - persisted
  const [activeTab, setActiveTabState] = useState(() => getPersistedState('activeTab', 'home'));
  const [menuScreen, setMenuScreen] = useState(null); // 'profile' | 'payment' | 'support'
  const [supportSubScreen, setSupportSubScreen] = useState(null);

  // Wrapper to persist activeTab
  const setActiveTab = useCallback((tab) => {
    setActiveTabState(tab);
    persistState('activeTab', tab);
  }, []);

  // Notifications
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsFetched, setNotificationsFetched] = useState(false);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    try {
      const data = await notificationsApi.getAll();
      setNotifications(data || []);
      setNotificationsFetched(true);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      // Don't show error to user, just use empty array
      setNotifications([]);
    }
  }, []);

  // Groups - some persisted
  const [groupsSubTab, setGroupsSubTabState] = useState(() => getPersistedState('groupsSubTab', 'public'));
  const [selectedGroupState, setSelectedGroupState] = useState(null);
  // Initialize with persisted joined group IDs (UUIDs from API)
  const [joinedGroupIds, setJoinedGroupIdsState] = useState(() => getPersistedState('joinedGroupIds', []));
  const [joinedGroupsFetched, setJoinedGroupsFetched] = useState(false);
  const [groupVisitTimestamps, setGroupVisitTimestamps] = useState(() => getPersistedState('groupVisitTimestamps', {}));

  // Fetch user's joined groups from API on mount
  const fetchJoinedGroups = useCallback(async () => {
    try {
      console.log('Fetching user joined groups from API...');
      const groups = await groupsApi.getAll('JOINED');
      console.log('Fetched joined groups:', groups);

      // Extract UUIDs from the groups
      const groupUuids = groups.map((g) => g.uuid || g.id).filter(Boolean);
      console.log('Joined group UUIDs:', groupUuids);

      setJoinedGroupIdsState(groupUuids);
      persistState('joinedGroupIds', groupUuids);
      setJoinedGroupsFetched(true);
      return groupUuids;
    } catch (err) {
      console.error('Failed to fetch joined groups:', err);
      setJoinedGroupsFetched(true);
      return [];
    }
  }, []);

  // Fetch joined groups on mount
  useEffect(() => {
    fetchJoinedGroups();
  }, [fetchJoinedGroups]);

  // Wrapper to track group visits (supports both mock groups with id and API groups with uuid)
  const setSelectedGroup = useCallback((group) => {
    setSelectedGroupState(group);
    if (group) {
      const groupKey = group.uuid || group.id;
      if (groupKey) {
        setGroupVisitTimestamps((prev) => {
          const updated = { ...prev, [groupKey]: Date.now() };
          persistState('groupVisitTimestamps', updated);
          return updated;
        });
      }
    }
  }, []);
  const [showGroupDealsDrawer, setShowGroupDealsDrawer] = useState(false);
  const [showAddDealDrawer, setShowAddDealDrawer] = useState(false);
  const [showGroupOverviewDrawer, setShowGroupOverviewDrawer] = useState(false);
  const [groupChatRefreshTrigger, setGroupChatRefreshTrigger] = useState(0);

  // Function to trigger group chat refresh (called after successful payment)
  const triggerGroupChatRefresh = useCallback(() => {
    setGroupChatRefreshTrigger(prev => prev + 1);
  }, []);

  // API Groups - fetched from Boldscale API (shared across tabs)
  const [apiGroups, setApiGroups] = useState([]);
  const [loadingApiGroups, setLoadingApiGroups] = useState(false);
  const [apiGroupsError, setApiGroupsError] = useState(null);
  const [apiGroupsFetched, setApiGroupsFetched] = useState(false);

  // Fetch groups from API (can be called from any component)
  const fetchApiGroups = useCallback(async (forceRefresh = false) => {
    // Skip if already fetched and not forcing refresh
    if (apiGroupsFetched && !forceRefresh) {
      return apiGroups;
    }

    setLoadingApiGroups(true);
    setApiGroupsError(null);
    try {
      // Fetch groups and BonndleGroups avatars in parallel
      const [fetchedGroups, bonndleAvatars] = await Promise.all([
        groupsApi.getAll('ALL'),
        bonndleGroupsApi.getAvatars(),
      ]);
      console.log('Fetched groups from API:', fetchedGroups);
      console.log('Fetched BonndleGroups avatars:', bonndleAvatars);

      // Fetch details for each group
      const groupsWithDetails = await Promise.all(
        (fetchedGroups || []).map(async (group) => {
          try {
            const groupId = group.uuid || group.id;
            const details = await groupsApi.getOne(groupId);
            return {
              ...group,
              ...details,
              member_count: details.members?.length || group.member_count || 0,
              deals: details.deals || [],
            };
          } catch (err) {
            console.warn(`Failed to fetch details for group ${group.name}:`, err);
            return group;
          }
        })
      );

      // Debug: Log all group IDs and available BonndleGroups IDs
      console.log('BonndleGroups avatar keys:', Object.keys(bonndleAvatars));
      console.log('Boldscale group UUIDs:', groupsWithDetails.map(g => ({ name: g.name, uuid: g.uuid, id: g.id })));

      // Merge avatars: only use BonndleGroups avatar if Boldscale didn't provide one
      const mergedGroups = groupsWithDetails.map((group) => {
        const groupId = group.uuid || group.id;
        // Check all possible image field names - only consider non-empty strings as "existing"
        const existingImage = group.image_url || group.avatar || group.image;
        const hasExistingImage = existingImage && typeof existingImage === 'string' && existingImage.trim() !== '';

        console.log(`Group "${group.name}": uuid=${groupId}, hasExistingImage=${hasExistingImage}, hasBonndleAvatar=${!!bonndleAvatars[groupId]}`);

        if (!hasExistingImage && bonndleAvatars[groupId]) {
          console.log(`✓ Using BonndleGroups avatar for group: ${group.name} (groupId: ${groupId})`);
          return {
            ...group,
            image_url: bonndleAvatars[groupId],
            avatar: bonndleAvatars[groupId],
            image: bonndleAvatars[groupId],
          };
        }
        return group;
      });

      setApiGroups(mergedGroups);
      setApiGroupsFetched(true);
      return mergedGroups;
    } catch (err) {
      console.error('Failed to fetch groups:', err);
      if (err.status === 401) {
        setApiGroupsError('Please log in to view groups');
      } else {
        setApiGroupsError('Failed to load groups');
      }
      setApiGroups([]);
      return [];
    } finally {
      setLoadingApiGroups(false);
    }
  }, [apiGroups, apiGroupsFetched]);

  // Fetch a single group by ID from the API and select it
  const fetchAndSelectGroup = useCallback(async (groupId) => {
    if (!groupId) {
      console.warn('fetchAndSelectGroup: No groupId provided');
      return null;
    }

    try {
      console.log('Fetching group from API:', groupId);
      const group = await groupsApi.getOne(groupId);
      console.log('Fetched group:', group);

      if (group) {
        // Normalize the group data for the app
        const normalizedGroup = {
          id: group.uuid || group.id || groupId,
          uuid: group.uuid || groupId,
          name: group.name || 'Group',
          description: group.description || '',
          members: group.member_count || group.members?.length || 0,
          member_count: group.member_count || group.members?.length || 0,
          deals: group.deals || [],
          image: group.image || group.avatar,
          ...group,
        };
        setSelectedGroup(normalizedGroup);
        return normalizedGroup;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch group:', error);
      // Fallback: create a minimal group object
      const fallbackGroup = {
        id: groupId,
        uuid: groupId,
        name: 'Group',
        members: 0,
        description: '',
      };
      setSelectedGroup(fallbackGroup);
      return fallbackGroup;
    }
  }, [setSelectedGroup]);

  // Teams - user's workplace/organization teams
  const [userTeamIds, setUserTeamIds] = useState(['team-1', 'team-2', 'team-3']);
  const userTeams = TEAMS_LIST.filter((t) => userTeamIds.includes(t.id));

  // Wrappers to persist groups state
  const setGroupsSubTab = useCallback((tab) => {
    setGroupsSubTabState(tab);
    persistState('groupsSubTab', tab);
  }, []);

  const setJoinedGroupIds = useCallback((idsOrUpdater) => {
    setJoinedGroupIdsState((prev) => {
      const newIds = typeof idsOrUpdater === 'function' ? idsOrUpdater(prev) : idsOrUpdater;
      // Persist to localStorage
      persistState('joinedGroupIds', newIds);
      return newIds;
    });
  }, []);

  // Shop/Deals - some persisted
  const [shopSubTab, setShopSubTabState] = useState(() => getPersistedState('shopSubTab', 'deals'));

  // Wrapper to persist shop state
  const setShopSubTab = useCallback((tab) => {
    setShopSubTabState(tab);
    persistState('shopSubTab', tab);
  }, []);

  // Stores - some persisted
  const [storesSubTab, setStoresSubTabState] = useState(() => getPersistedState('storesSubTab', 'deals'));

  // Wrapper to persist stores state
  const setStoresSubTab = useCallback((tab) => {
    setStoresSubTabState(tab);
    persistState('storesSubTab', tab);
  }, []);

  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showDealInfoDrawer, setShowDealInfoDrawer] = useState(false);
  const [isolatedGroupDeals, setIsolatedGroupDeals] = useState(INITIAL_ISOLATED_DEALS);

  // Pay Bill Slider
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showPayBillSlider, setShowPayBillSlider] = useState(false);

  // Feed - fetched from Wix API
  const [feedData, setFeedData] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true); // Start with loading true
  const [feedError, setFeedError] = useState(null);
  const [feedFetched, setFeedFetched] = useState(false);

  // Fetch feed from Wix API
  const fetchFeed = useCallback(async (forceRefresh = false) => {
    setFeedLoading(true);
    setFeedError(null);
    try {
      const data = await wixFeedApi.getFeed();
      console.log('Fetched feed from Wix API:', data);
      if (data && data.length > 0) {
        setFeedData(data);

        // Fetch real comment and like counts for each feed item in background
        // This ensures we show accurate counts even if feed item counts are stale
        const userId = getUserId();
        data.forEach(async (item) => {
          try {
            // Fetch comment count
            const commentResult = await wixCommentsApi.getComments(item.id, 1, 0); // Just get count, limit 1
            if (commentResult.totalCount !== undefined && commentResult.totalCount !== item.comments) {
              setFeedData((prev) =>
                prev.map((feedItem) =>
                  feedItem.id === item.id
                    ? { ...feedItem, comments: commentResult.totalCount }
                    : feedItem
                )
              );
            }

            // Fetch like count AND user's like status (isLiked)
            const likeResult = await wixLikesApi.getLikeStatus(item.id, userId);
            if (likeResult.likes !== undefined || likeResult.isLiked !== undefined) {
              setFeedData((prev) =>
                prev.map((feedItem) =>
                  feedItem.id === item.id
                    ? {
                        ...feedItem,
                        likes: likeResult.likes ?? feedItem.likes,
                        isLiked: likeResult.isLiked ?? feedItem.isLiked,
                      }
                    : feedItem
                )
              );
            }
          } catch (err) {
            // Silently fail - feed already has fallback counts
          }
        });
      } else {
        // Fallback to INITIAL_FEED if API returns empty
        console.log('API returned empty, using fallback feed');
        setFeedData(INITIAL_FEED);
      }
      setFeedFetched(true);
      return data;
    } catch (err) {
      console.error('Failed to fetch feed from Wix:', err);
      setFeedError('Failed to load feed');
      // Use INITIAL_FEED as fallback on error
      setFeedData(INITIAL_FEED);
      return INITIAL_FEED;
    } finally {
      setFeedLoading(false);
    }
  }, []);

  // Fetch feed on mount
  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  // Refresh feed when user logs in (to update like status for the logged-in user)
  useEffect(() => {
    const handleUserLogin = () => {
      console.log('[AppState] User logged in, refreshing feed with user-specific data...');
      fetchFeed(true);
    };

    window.addEventListener('bonndle:user:login', handleUserLogin);
    return () => window.removeEventListener('bonndle:user:login', handleUserLogin);
  }, [fetchFeed]);

  // Feed Comments - for stories, insights, and tips
  const [feedComments, setFeedComments] = useState({
    'story-1': [
      {
        id: 1,
        author: 'Jordan P.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jordan',
        text: 'Congrats! BillSavers has been a game-changer for me too.',
        time: 'Mar 24, 2026',
        timestamp: Date.now() - 86400000 * 6,
      },
      {
        id: 2,
        author: 'Kim L.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Kim',
        text: "That's awesome! I'm at $75 so far. Almost there!",
        time: 'Mar 24, 2026',
        timestamp: Date.now() - 86400000 * 6,
      },
    ],
    'story-2': [
      {
        id: 1,
        author: 'Ryan T.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Ryan',
        text: 'Great tip! I never thought about timing my purchases.',
        time: 'Mar 22, 2026',
        timestamp: Date.now() - 86400000 * 8,
      },
    ],
    'insight-1': [
      {
        id: 1,
        author: 'Priya S.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Priya',
        text: 'This is why I love Bonndle. Collective buying power!',
        time: 'Mar 20, 2026',
        timestamp: Date.now() - 86400000 * 10,
      },
      {
        id: 2,
        author: 'Marcus T.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Marcus',
        text: '18% more savings is huge over a year!',
        time: 'Mar 20, 2026',
        timestamp: Date.now() - 86400000 * 10,
      },
    ],
    'story-3': [
      {
        id: 1,
        author: 'Nina C.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Nina',
        text: 'Our team did this too! Coordination is key.',
        time: 'Mar 18, 2026',
        timestamp: Date.now() - 86400000 * 12,
      },
    ],
    'story-4': [
      {
        id: 1,
        author: 'Tom R.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Tom',
        text: 'Just joined HealthFam because of this post!',
        time: 'Mar 15, 2026',
        timestamp: Date.now() - 86400000 * 15,
      },
    ],
  });

  // Group Messages - shared between chat and deal comments
  const [groupMessages, setGroupMessages] = useState({
    // Pre-populate with some sample messages
    9: [
      {
        id: 1,
        type: 'message',
        author: 'Sarah M.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah',
        text: 'Hey team! Just saw the new Verizon deal drop.',
        time: 'Mar 28, 2026',
        timestamp: Date.now() - 86400000 * 2,
      },
      {
        id: 2,
        type: 'deal_comment',
        dealId: 'deal-9-1',
        dealBrand: 'Verizon',
        author: 'Mike T.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Mike',
        text: 'Just used this deal yesterday, the cashback came through!',
        time: 'Mar 28, 2026',
        timestamp: Date.now() - 86400000 * 2,
      },
      {
        id: 3,
        type: 'deal_comment',
        dealId: 'deal-9-1',
        dealBrand: 'Verizon',
        author: 'Lisa K.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Lisa',
        text: 'How long does the cashback usually take to process?',
        time: 'Mar 27, 2026',
        timestamp: Date.now() - 86400000 * 3,
      },
      {
        id: 4,
        type: 'deal_comment',
        dealId: 'deal-9-1',
        dealBrand: 'Verizon',
        author: 'Sarah M.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah',
        text: '@Lisa usually 2-3 business days in my experience',
        time: 'Mar 27, 2026',
        timestamp: Date.now() - 86400000 * 3,
      },
    ],
    1: [
      {
        id: 1,
        type: 'message',
        author: 'Alex J.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alex',
        text: 'Who else is doing the Whole Foods haul this week?',
        time: 'Mar 26, 2026',
        timestamp: Date.now() - 86400000 * 4,
      },
      {
        id: 2,
        type: 'deal_comment',
        dealId: 'deal-1-2',
        dealBrand: 'Whole Foods',
        author: 'Emma R.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Emma',
        text: 'Count me in! The 10% cashback is amazing.',
        time: 'Mar 26, 2026',
        timestamp: Date.now() - 86400000 * 4,
      },
    ],
  });

  // Profile form - no defaults, populated from API
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    gender: '',
    birthYear: '',
    country: '',
    bonndleTag: '',
  });

  // Payment
  const [savedCards, setSavedCards] = useState([
    { id: 1, type: 'visa', last4: '4242', expiry: '12/26', isDefault: true },
    { id: 2, type: 'mastercard', last4: '8888', expiry: '03/25', isDefault: false },
  ]);
  const [payWithRewardsEnabled, setPayWithRewardsEnabled] = useState(true);
  const [showCardForm, setShowCardForm] = useState(false);

  // Derived values - check all possible field names for read status
  const unreadCount = notifications.filter((n) => {
    const isRead = n.hasRead || n.is_read || n.isRead || n.read || false;
    return !isRead;
  }).length;
  const isInChat = !!selectedGroupState;
  const joinedGroups = GROUPS_LIST.filter((g) => joinedGroupIds.includes(g.id));
  const publicGroups = GROUPS_LIST.filter((g) => g.status === 'Public');
  const privateGroups = GROUPS_LIST.filter((g) => g.status === 'Private');

  // Actions
  const toggleLike = useCallback((id) => {
    const userId = getUserId();

    // Optimistic update - immediate UI feedback
    setFeedData((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, isLiked: !item.isLiked, likes: item.isLiked ? item.likes - 1 : item.likes + 1 }
          : item
      )
    );

    // Persist to Wix backend in background (don't await to keep UI responsive)
    // Pass userId so backend can track which user liked/unliked and prevent duplicates
    wixLikesApi.toggleLike(id, userId).catch((error) => {
      console.error('[AppState] Failed to persist like to Wix API:', error);
      // Note: We keep the optimistic update even on error
    });
  }, []);

  const markAsRead = useCallback((id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const joinGroup = useCallback((groupId) => {
    setJoinedGroupIds((prev) => {
      if (prev.includes(groupId)) return prev;
      return [...prev, groupId];
    });
  }, [setJoinedGroupIds]);

  const leaveGroup = useCallback((groupId) => {
    setJoinedGroupIds((prev) => prev.filter((id) => id !== groupId));
    if (selectedGroupState?.id === groupId) {
      setSelectedGroupState(null);
    }
  }, [selectedGroupState, setJoinedGroupIds]);

  // Join group from deal and route to group chat
  const confirmJoinGroup = useCallback(async (deal) => {
    const groupId = deal.groupId;
    if (!groupId) return;

    // Try to find in GROUPS_LIST (mock data) or use groupId directly (API UUID)
    const groupToJoin = GROUPS_LIST.find((g) => g.id === groupId);

    // Add groupId to joined list if not already joined
    setJoinedGroupIds((prev) => {
      if (prev.includes(groupId)) return prev;
      return [...prev, groupId];
    });

    // Call API to join the group
    try {
      await groupsApi.join(groupId);
      console.log('Successfully joined group:', groupId);
    } catch (err) {
      console.error('Failed to join group via API:', err);
      // Continue anyway - local state is updated
    }

    // Select the group if found in mock data, otherwise it will be fetched later
    if (groupToJoin) {
      setSelectedGroup(groupToJoin);
    }

    // Close drawers
    setShowDealInfoDrawer(false);
    setSelectedDeal(null);
  }, [setJoinedGroupIds]);

  const addDeal = useCallback((groupId, dealData) => {
    const newDeal = {
      id: `new-${Date.now()}`,
      groupId,
      ...dealData,
    };
    setIsolatedGroupDeals((prev) => ({
      ...prev,
      [groupId]: [...(prev[groupId] || []), newDeal],
    }));
  }, []);

  // Add a message to a group chat (can be regular message or deal comment)
  const addGroupMessage = useCallback((groupId, messageData) => {
    const newMessage = {
      id: Date.now(),
      timestamp: Date.now(),
      time: 'Just now',
      ...messageData,
    };
    setGroupMessages((prev) => ({
      ...prev,
      [groupId]: [...(prev[groupId] || []), newMessage],
    }));
  }, []);

  // Get messages for a specific group
  const getGroupMessages = useCallback((groupId) => {
    return groupMessages[groupId] || [];
  }, [groupMessages]);

  // Get deal-specific comments for a group
  const getDealComments = useCallback((groupId, dealId) => {
    const messages = groupMessages[groupId] || [];
    return messages.filter((m) => m.type === 'deal_comment' && m.dealId === dealId);
  }, [groupMessages]);

  // Track which feed items have had their comments fetched
  const fetchedCommentsRef = useRef(new Set());

  // Add a comment to a feed item (story, insight, tip)
  const addFeedComment = useCallback(async (feedItemId, commentData) => {
    // Create optimistic local comment
    const newComment = {
      id: Date.now(),
      timestamp: Date.now(),
      time: 'Just now',
      ...commentData,
    };

    // Update local state immediately (optimistic update)
    setFeedComments((prev) => ({
      ...prev,
      [feedItemId]: [...(prev[feedItemId] || []), newComment],
    }));

    // Also update the comment count in feed data
    setFeedData((prev) =>
      prev.map((item) =>
        item.id === feedItemId
          ? { ...item, comments: (item.comments || 0) + 1 }
          : item
      )
    );

    // Send to API in background (don't await to keep UI responsive)
    try {
      await wixCommentsApi.addComment(feedItemId, commentData);
      console.log('[AppState] Comment saved to API');
    } catch (error) {
      console.error('[AppState] Failed to save comment to API:', error);
      // Comment is already shown locally, API sync can retry later
    }
  }, []);

  // Fetch comments for a feed item from API
  const fetchFeedComments = useCallback(async (feedItemId) => {
    // Skip if already fetched
    if (fetchedCommentsRef.current.has(feedItemId)) {
      return feedComments[feedItemId] || [];
    }

    try {
      console.log('[AppState] Fetching comments for feed item:', feedItemId);
      const result = await wixCommentsApi.getComments(feedItemId);

      if (result.comments && result.comments.length > 0) {
        // Merge API comments with any local comments (avoid duplicates)
        setFeedComments((prev) => {
          const localComments = prev[feedItemId] || [];
          const localIds = new Set(localComments.map((c) => c.id));
          const newComments = result.comments.filter((c) => !localIds.has(c.id));
          return {
            ...prev,
            [feedItemId]: [...result.comments, ...localComments.filter((c) => !result.comments.find((r) => r.id === c.id))],
          };
        });
      }

      fetchedCommentsRef.current.add(feedItemId);
      return result.comments;
    } catch (error) {
      console.error('[AppState] Failed to fetch comments:', error);
      return feedComments[feedItemId] || [];
    }
  }, [feedComments]);

  // Get comments for a feed item (returns local state, use fetchFeedComments to load from API)
  const getFeedComments = useCallback((feedItemId) => {
    return feedComments[feedItemId] || [];
  }, [feedComments]);

  const value = {
    // Navigation
    activeTab,
    setActiveTab,
    menuScreen,
    setMenuScreen,
    supportSubScreen,
    setSupportSubScreen,

    // Notifications
    showNotifications,
    setShowNotifications,
    notifications,
    unreadCount,
    markAsRead,
    markAllRead,
    fetchNotifications,

    // Groups
    groupsSubTab,
    setGroupsSubTab,
    selectedGroup: selectedGroupState,
    setSelectedGroup,
    fetchAndSelectGroup,
    joinedGroupIds,
    joinedGroups,
    fetchJoinedGroups,
    groupVisitTimestamps,
    publicGroups,
    privateGroups,
    joinGroup,
    leaveGroup,
    confirmJoinGroup,
    showGroupDealsDrawer,
    setShowGroupDealsDrawer,
    showAddDealDrawer,
    setShowAddDealDrawer,
    showGroupOverviewDrawer,
    setShowGroupOverviewDrawer,
    groupChatRefreshTrigger,
    triggerGroupChatRefresh,
    isInChat,

    // API Groups (from Boldscale)
    apiGroups,
    setApiGroups,
    loadingApiGroups,
    setLoadingApiGroups,
    apiGroupsError,
    setApiGroupsError,
    fetchApiGroups,

    // Teams
    userTeams,
    userTeamIds,
    setUserTeamIds,
    TEAMS_LIST,

    // Shop/Deals
    shopSubTab,
    setShopSubTab,
    storesSubTab,
    setStoresSubTab,
    selectedDeal,
    setSelectedDeal,
    showDealInfoDrawer,
    setShowDealInfoDrawer,
    isolatedGroupDeals,
    addDeal,

    // Pay Bill Slider
    selectedProvider,
    setSelectedProvider,
    showPayBillSlider,
    setShowPayBillSlider,

    // Group Messages
    groupMessages,
    addGroupMessage,
    getGroupMessages,
    getDealComments,

    // Feed
    feedData,
    feedLoading,
    feedError,
    fetchFeed,
    toggleLike,
    feedComments,
    addFeedComment,
    getFeedComments,
    fetchFeedComments,

    // Profile
    profileForm,
    setProfileForm,

    // Payment
    savedCards,
    setSavedCards,
    payWithRewardsEnabled,
    setPayWithRewardsEnabled,
    showCardForm,
    setShowCardForm,

    // Constants
    GROUPS_LIST,
  };

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useAppState() {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
}

export default AppStateContext;
